import React from "react";
import { useTranslation, Trans, TransProps } from "react-i18next";
import {
  Grid2,
  Box,
  useTheme,
  Typography,
  styled,
  Divider,
  Link,
  Container as MuiContainer,
} from "@mui/material";
import { useMandatorContext } from "@rentablo/theming";
import { useFeatureFlag } from "@hooks";
import { TermsMainTitle, TermsTitle, TermsLinkStyled } from "@components";
import { PRIVACY } from "@helpers/urlConstants";
import { ImpressumCard } from "@features/impressum/ImpressumCard";

const Container = styled(MuiContainer)(({ theme }) => ({
  paddingTop: theme.spacing(35),
  paddingBottom: theme.spacing(40),
  [theme.breakpoints.down("md")]: {
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(20),
  },
}));

const StyledBlock = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(11),
  [theme.breakpoints.down("md")]: {
    marginBottom: theme.spacing(7),
  },
}));

const HeadingWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(11),
  [theme.breakpoints.down("md")]: {
    marginTop: theme.spacing(7),
  },
}));

const DividerWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(11),
  [theme.breakpoints.down("md")]: {
    marginBottom: theme.spacing(7),
  },
}));

const TransWithNamespace = ({ i18nKey, ...rest }: TransProps<any>) => {
  const { isRentablo } = useMandatorContext();
  const namespace = isRentablo ? "impressumPage" : "impressumPageForMandators";

  return <Trans i18nKey={`${namespace}.${i18nKey}`} {...rest} />;
};

export const Impressum = () => {
  const { isRentablo } = useMandatorContext();
  const isCashbackEnabled = useFeatureFlag("CASHBACK_WIZARD");
  const namespace = isRentablo ? "impressumPage" : "impressumPageForMandators";

  const { t, i18n } = useTranslation("translation", {
    keyPrefix: namespace,
  });
  const { t: tGlobal } = useTranslation();
  const theme = useTheme();

  return (
    <Container>
      <Grid2 container>
        {isRentablo && (
          <>
            <Grid2
              size={{
                xs: 12,
                lg: 5,
              }}
            >
              <TermsMainTitle variant="h1">{t("title")}</TermsMainTitle>

              <TermsTitle variant="h4" mb={4}>
                {t("mailAddressTitle")}
              </TermsTitle>
              <Typography>
                {t("companyName")} {t("companyCustomerService")}
              </Typography>
              <Typography color="textSecondary" whiteSpace="pre-line">
                {t("mailAddress")}
              </Typography>

              <Box
                sx={{ backgroundColor: theme.palette.background.selected }}
                borderRadius="4px"
                padding="8px 12px"
                width="fit-content"
                mb={6}
                mt={theme.breakpoints.down("md") ? 6 : 11}
                whiteSpace="pre-line"
              >
                <Typography color={theme.palette.primary.dark}>
                  {t("sendToCustomerService")}
                </Typography>
              </Box>
            </Grid2>
            <Grid2
              size={{
                lg: 1,
              }}
            />
          </>
        )}

        <Grid2
          size={{
            xs: 12,
            lg: isRentablo ? 6 : 8,
          }}
        >
          {!isRentablo && <Typography variant="h1">{t("title")}</Typography>}
          <ImpressumCard
            namespace={
              isRentablo ? "impressumPage" : "impressumPageForMandators"
            }
          />
        </Grid2>
      </Grid2>
      <Grid2 container>
        <Grid2
          size={{
            xs: 12,
            lg: 8,
          }}
        >
          <StyledBlock>
            <Typography
              sx={{ hyphens: "auto" }}
              component="div"
              variant="body3"
              color="textSecondary"
            >
              <TransWithNamespace
                i18nKey={
                  isCashbackEnabled ? "section1" : "section1_withoutCashback"
                }
              >
                {[
                  <TermsTitle variant="h4" key="heading" />,
                  <TermsLinkStyled
                    key="link"
                    href="https://portal.mvp.bafin.de/database/ZahlInstInfo/zahlinst.do?id=152048"
                    target="_blank"
                    rel="noreferrer"
                    fontWeight="600"
                    sx={{ wordWrap: "break-word" }}
                  />,
                  <Typography
                    variant="h5"
                    key="small-heading"
                    color="textPrimary"
                    fontWeight={600}
                  />,
                ]}
              </TransWithNamespace>
            </Typography>
          </StyledBlock>

          <StyledBlock>
            <Typography component="div" variant="body3" color="textSecondary">
              <TransWithNamespace i18nKey="section2">
                {[
                  <Typography
                    key="heading"
                    variant="h6"
                    color="textPrimary"
                    mb={3}
                  />,
                ]}
              </TransWithNamespace>
            </Typography>
          </StyledBlock>

          <StyledBlock>
            <Typography
              component="div"
              variant="body3"
              color="textSecondary"
              whiteSpace="pre-line"
            >
              <TransWithNamespace i18nKey="section3">
                {[
                  <Typography
                    key="heading"
                    variant="h6"
                    color="textPrimary"
                    mb={3}
                  />,
                ]}
              </TransWithNamespace>
            </Typography>
          </StyledBlock>

          <StyledBlock>
            <Typography
              component="div"
              variant="body3"
              color="textSecondary"
              whiteSpace="pre-line"
            >
              <TransWithNamespace i18nKey="section4">
                {[
                  <Typography
                    key="heading"
                    variant="h6"
                    color="textPrimary"
                    mb={3}
                  />,
                ]}
              </TransWithNamespace>
            </Typography>
          </StyledBlock>

          <StyledBlock>
            <Typography
              component="div"
              variant="body3"
              color="textSecondary"
              whiteSpace="pre-line"
            >
              <TransWithNamespace i18nKey="section5">
                {[
                  <Typography
                    key="heading"
                    variant="h6"
                    color="textPrimary"
                    mb={3}
                  />,
                  <Typography
                    variant="body3"
                    color="textPrimary"
                    key="caption"
                  />,
                ]}
              </TransWithNamespace>
            </Typography>
          </StyledBlock>

          <StyledBlock>
            <Typography
              component="div"
              variant="body3"
              color="textSecondary"
              whiteSpace="pre-line"
            >
              <TransWithNamespace i18nKey="section6.1">
                {[
                  <Typography
                    key="heading"
                    variant="h6"
                    color="textPrimary"
                    mb={3}
                  />,
                  <Typography
                    variant="body3"
                    color="textPrimary"
                    key="caption"
                  />,
                  <TermsLinkStyled
                    key="link"
                    href="https://www.berlin.ihk.de"
                    target="_blank"
                    rel="noreferrer"
                  />,
                ]}
              </TransWithNamespace>
            </Typography>
            <Typography
              mt={3}
              component="div"
              variant="body3"
              color="textSecondary"
              whiteSpace="pre-line"
            >
              <TransWithNamespace i18nKey="section6.2">
                {[
                  <TermsLinkStyled
                    key="link"
                    href="https://www.vermittlerregister.info"
                    target="_blank"
                    rel="noreferrer"
                  />,
                ]}
              </TransWithNamespace>
            </Typography>
          </StyledBlock>

          <StyledBlock>
            <Typography
              component="div"
              variant="body3"
              color="textSecondary"
              whiteSpace="pre-line"
            >
              <TransWithNamespace i18nKey="section7">
                {[
                  <Typography
                    key="heading"
                    variant="h6"
                    color="textPrimary"
                    mb={3}
                  />,
                ]}
              </TransWithNamespace>
            </Typography>
          </StyledBlock>

          <DividerWrapper>
            <Divider />
          </DividerWrapper>

          <Typography
            mb={7}
            component="div"
            variant="body3"
            color="textSecondary"
            whiteSpace="pre-line"
          >
            <TransWithNamespace i18nKey="section8">
              {[
                <Typography
                  key="heading"
                  variant="h4"
                  color="textPrimary"
                  mb={6}
                />,
                <TermsLinkStyled
                  key="link"
                  href="https://ec.europa.eu/consumers/odr"
                  target="_blank"
                  rel="noreferrer"
                />,
              ]}
            </TransWithNamespace>
          </Typography>

          <Typography
            mb={7}
            variant="body3"
            component="div"
            whiteSpace="pre-line"
          >
            <TransWithNamespace i18nKey="section9">
              {[
                <Typography
                  key="heading"
                  variant="h6"
                  color="textPrimary"
                  mb={6}
                />,
                <Typography
                  variant="body3"
                  color="textSecondary"
                  key="subheading"
                />,
                <TermsLinkStyled
                  key="link"
                  href="https://www.versicherungsombudsmann.de"
                  target="_blank"
                  rel="noreferrer"
                />,
                <Typography
                  key="subheading"
                  variant="body3"
                  color="textSecondary"
                />,
                <TermsLinkStyled
                  key="link"
                  href="https://www.pkv-ombudsmann.de"
                  target="_blank"
                  rel="noreferrer"
                />,
                <Typography
                  key="subheading"
                  variant="body3"
                  color="textSecondary"
                />,
                <TermsLinkStyled
                  key="link"
                  href="https://www.ombudsstelle-investmentfonds.de"
                  target="_blank"
                  rel="noreferrer"
                />,
              ]}
            </TransWithNamespace>
          </Typography>

          <Typography
            mb={7}
            variant="body3"
            component="div"
            color="textSecondary"
            whiteSpace="pre-line"
          >
            <TransWithNamespace i18nKey="section10">
              {[
                <Typography
                  key="heading"
                  variant="h6"
                  color="textPrimary"
                  mb={3}
                />,
                <TermsLinkStyled
                  key="email"
                  href={`mailto:${t("aboutCompany.support.email")}`}
                />,
              ]}
            </TransWithNamespace>
          </Typography>

          <Typography
            mb={7}
            variant="body3"
            component="div"
            color="textSecondary"
            whiteSpace="pre-line"
          >
            <TransWithNamespace i18nKey="section11">
              {[
                <Typography
                  key="heading"
                  variant="h6"
                  color="textPrimary"
                  mb={3}
                />,
                <TermsLinkStyled
                  key="link"
                  href="https://www.gesetze-im-internet.de"
                  target="_blank"
                  rel="noreferrer"
                />,
                <Typography
                  variant="body3"
                  color="textPrimary"
                  key="caption"
                />,
              ]}
            </TransWithNamespace>
          </Typography>

          <Typography
            mb={9}
            variant="body3"
            component="div"
            color="textSecondary"
            whiteSpace="pre-line"
          >
            <TransWithNamespace i18nKey="section12">
              {[
                <Typography
                  key="heading"
                  variant="h6"
                  color="textPrimary"
                  mb={3}
                />,
                <Typography
                  variant="body3"
                  color="textPrimary"
                  key="caption"
                />,
                <TermsLinkStyled
                  key="link"
                  href="https://www.verbraucher-schlichter.de"
                  target="_blank"
                  rel="noreferrer"
                />,
              ]}
            </TransWithNamespace>
          </Typography>

          <DividerWrapper>
            <Divider />
          </DividerWrapper>

          <Typography variant="h4" mb={3}>
            {t("disclaimer")}
          </Typography>
          <Typography
            mb={7}
            variant="body3"
            component="div"
            color="textSecondary"
          >
            {t("disclaimerText")}
          </Typography>
          <Typography variant="h6" mb={3}>
            {t("liability")}
          </Typography>
          <Typography
            mb={7}
            variant="body3"
            component="div"
            color="textSecondary"
          >
            {t("liabilityText")}
          </Typography>
          <Typography variant="h6" mb={3}>
            {t("limited")}
          </Typography>
          <Typography
            mb={7}
            variant="body3"
            component="div"
            color="textSecondary"
          >
            {t("limitedText")}
          </Typography>

          <HeadingWrapper>
            <Typography variant="h4" mb={6}>
              {t("copyright")}
            </Typography>
          </HeadingWrapper>
          <Typography variant="body3" component="div" color="textSecondary">
            <TransWithNamespace i18nKey="copyrightText">
              {[
                <TermsLinkStyled
                  key="email"
                  href="mailto:presse@rentablo.de"
                />,
              ]}
            </TransWithNamespace>
          </Typography>

          <HeadingWrapper>
            <Typography variant="h4" mb={6}>
              {t("privacy")}
            </Typography>
          </HeadingWrapper>
          <Typography
            variant="body3"
            component="div"
            color="textSecondary"
            whiteSpace="pre-line"
          >
            <TransWithNamespace i18nKey="privacyText">
              {[
                <Link
                  key="link-0"
                  href={`${import.meta.env.VITE_APP_V2_URL}${PRIVACY}`}
                />,
                <Link
                  key="link-1"
                  href={`${import.meta.env.VITE_APP_V2_URL}${PRIVACY}`}
                />,
              ]}
            </TransWithNamespace>
          </Typography>

          <Typography
            whiteSpace="pre-line"
            variant="body1"
            color="textSecondary"
            mt={7}
          >
            {t("bottomText")}
          </Typography>
          {i18n.language === "en" && (
            <Typography variant="body1" color="textSecondary" mt={10}>
              {tGlobal("common.englishLegalNote")}
            </Typography>
          )}
        </Grid2>
      </Grid2>
    </Container>
  );
};
